// NirmalX pages style their own links (CSS modules), so they use next/link rather than the Chakra-styled Link.
// eslint-disable-next-line no-restricted-imports
import NextLink from 'next/link';
import React from 'react';

import type { Block } from 'types/api/block';

import { ago, EMPTY_BLOCK_PERIOD_S, findValidator, shortHash } from '../nirmalx';
import css from './home.module.css';

interface Props {
  blocks: Array<Block> | undefined;
  freshHeight: number | undefined;
  now: number;
}

/** The head of the chain: the next block, being forged. Idle, it is sealed on the 60 s heartbeat. */
const ForgingCard = ({ latest, now }: { latest: Block; now: number }) => {
  const since = now > 0 ? Math.max(0, (now - new Date(latest.timestamp).getTime()) / 1000) : 0;
  const left = Math.ceil(EMPTY_BLOCK_PERIOD_S - since);
  return (
    <div className={ css.forgeCard } aria-label={ `Block ${ latest.height + 1 } is being forged` }>
      <span className={ css.forgeTag }><span className={ css.dot }/>Forging</span>
      <div className={ css.blockHeight }>#{ (latest.height + 1).toLocaleString('en-US') }</div>
      <div className={ css.forgeCubes } aria-hidden="true"><span/><span/><span/></div>
      <div className={ css.forgeBar } aria-hidden="true">
        <span style={{ transform: `scaleX(${ Math.min(1, since / EMPTY_BLOCK_PERIOD_S) })` }}/>
      </div>
      <div className={ css.forgeNote }>
        { now > 0 && left > 0 ? `Heartbeat in ~${ left }s` : 'Sealing…' }
        <span>Sooner with a transaction</span>
      </div>
    </div>
  );
};

/**
 * The chain, newest first, behind the block being forged. A block that arrives over the socket opens its slot
 * (pushing the older ones along) and lands with a flash; the oldest fade out at the far end. No scrolling.
 */
const BlockStream = ({ blocks, freshHeight, now }: Props) => {
  if (!blocks?.length) {
    return <div className={ css.empty }><div className={ css.emptyText }>Loading the latest blocks…</div></div>;
  }

  return (
    <div className={ css.stream }>
      { /* Keyed by height: each new block mounts a fresh forging card, which replays its entrance. */ }
      <ForgingCard key={ blocks[0].height } latest={ blocks[0] } now={ now }/>
      <span className={ `${ css.link } ${ css.linkPending }` } aria-hidden="true"/>
      { blocks.map((block, index) => {
        const isFresh = block.height === freshHeight;
        const proposer = findValidator(block.miner.hash);
        return (
          <div key={ block.height } className={ `${ css.slot } ${ isFresh ? css.slotFresh : '' }` }>
            <NextLink
              href={{ pathname: '/block/[height_or_hash]', query: { height_or_hash: String(block.height) } }}
              className={ `${ css.blockCard } ${ isFresh ? css.blockFresh : '' }` }
            >
              { isFresh && <span className={ css.newTag }>NEW</span> }
              <div className={ css.blockHeight }>#{ block.height.toLocaleString('en-US') }</div>
              <div className={ css.blockRow }><span>Sealed</span><b>{ now > 0 ? ago(block.timestamp, now) : '—' }</b></div>
              <div className={ css.blockRow }><span>Transactions</span><b>{ block.transactions_count }</b></div>
              <div className={ css.blockRow }><span>Proposer</span><b>{ proposer?.name ?? shortHash(block.miner.hash) }</b></div>
            </NextLink>
            { index < blocks.length - 1 && <span className={ css.link } aria-hidden="true"/> }
          </div>
        );
      }) }
    </div>
  );
};

export default React.memo(BlockStream);
